import { createContext } from 'react';
import type {
  FeatureFlagListResult,
  FeatureFlagQueryParams,
} from '@/feature-flags/FeatureFlagService';
import type {
  FeatureEvaluationResult,
  FeatureFlag,
} from '@/feature-flags/FeatureEvaluator';
import type {
  FeatureEvaluationContext,
  FeatureFlagEnvironment,
} from '@/feature-flags/FeatureStrategies';

export interface FeatureFlagContextValue {
  flags: FeatureFlag[];
  total: number;
  page: number;
  pageSize: number;
  isLoading: boolean;
  queryParams: FeatureFlagQueryParams;
  products: Array<{ id: string; name: string }>;
  environments: FeatureFlagEnvironment[];
  setQueryParams: (params: Partial<FeatureFlagQueryParams>) => void;
  queryFlags: (params?: Partial<FeatureFlagQueryParams>) => Promise<void>;
  createFlag: (
    payload: Omit<FeatureFlag, 'id' | 'createdDate' | 'lastModified'>,
  ) => Promise<FeatureFlag>;
  updateFlag: (
    id: string,
    payload: Partial<FeatureFlag>,
  ) => Promise<FeatureFlag | null>;
  deleteFlag: (id: string) => Promise<void>;
  updateRollout: (id: string, rolloutPercentage: number) => Promise<void>;
  toggleFlag: (id: string, enabled: boolean) => Promise<void>;
  refresh: () => Promise<void>;
  isEnabled: (key: string, context?: FeatureEvaluationContext) => boolean;
  evaluate: (
    key: string,
    context?: FeatureEvaluationContext,
  ) => FeatureEvaluationResult;
  subscribe: (subscriber: (flags: FeatureFlag[]) => void) => () => void;
  latestResult: FeatureFlagListResult | null;
}

export const FeatureFlagContext = createContext<
  FeatureFlagContextValue | undefined
>(undefined);
